import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet, TrendingUp } from "lucide-react";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fM = (v) => v ? `${v.toFixed(1)}` : "0";
const fPct = (v) => v ? `${v.toFixed(1)}%` : "—";

const COLORS = { "Amazon": "#f59e0b", "Flipkart": "#3b82f6", "Website/App": "#10b981", "FirstCry": "#8b5cf6" };

export default function ChannelPnL() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/financial/channel-pnl`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading || !data?.data) return <div className="h-64 skeleton-shimmer rounded" />;

  const channels = data.data || [];
  const periods = data.periods || [];
  const labels = data.labels || [];

  // Comparison chart
  const chartData = labels.map((label, i) => {
    const entry = { period: label };
    channels.forEach((ch) => { entry[ch.channel] = ch.net_revenue[periods[i]] || 0; });
    return entry;
  });

  return (
    <div className="space-y-5" data-testid="channel-pnl-page">
      <div>
        <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">Channel P&L</h1>
        <p className="text-sm text-slate-400 mt-1">Revenue &amp; profitability by sales channel &middot; INR Millions</p>
      </div>

      {/* Revenue by Channel Chart */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="channel-revenue-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-400" />Net Revenue by Channel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} barGap={1}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="period" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} />
              <Tooltip content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                    <p className="text-slate-300 font-medium mb-1">{label}</p>
                    {payload.map((p, i) => (
                      <p key={i} style={{ color: p.color }} className="font-mono">{p.name}: INR {p.value?.toFixed(1)}M</p>
                    ))}
                  </div>
                );
              }} />
              {channels.map((ch) => (
                <Bar key={ch.channel} dataKey={ch.channel} name={ch.channel} fill={COLORS[ch.channel] || "#64748b"} radius={[2, 2, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <AnnotationBanner title="Channel Strategy">
        <p>Website/App is the highest-margin channel (zero marketplace commission, no returns) and is projected to grow from INR {fM(channels.find(c => c.channel === "Website/App")?.net_revenue?.FY2025A)}M to INR {fM(channels.find(c => c.channel === "Website/App")?.net_revenue?.FY2030E)}M — becoming the single largest revenue contributor by FY30. Amazon remains the volume driver. Marketplace commission expenses don't apply to D2C, making every incremental website rupee significantly more profitable.</p>
      </AnnotationBanner>

      {/* Channel Tables */}
      {channels.map((ch) => (
        <Card key={ch.channel} className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid={`channel-${ch.channel.toLowerCase().replace(/[^a-z]/g, "")}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-space text-sm font-semibold text-slate-200 flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[ch.channel] }} />
                {ch.channel}
              </CardTitle>
              <Badge variant="outline" className="text-[10px] text-slate-400 border-slate-700">
                CM2 Margin FY30: {fPct(ch.cm2_margin?.FY2030E)}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-1.5 px-3 text-[10px] text-slate-500 uppercase w-44">Particulars</th>
                  {labels.map((l) => <th key={l} className="text-right py-1.5 px-3 text-[10px] text-slate-500 uppercase">{l}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  { label: "Net Revenue", key: "net_revenue", bold: true },
                  { label: "COGS", key: "cogs" },
                  { label: "Gross Profit", key: "gross_profit", bold: true },
                  { label: "GP Margin", key: "gp_margin", pct: true },
                  { label: "Freight", key: "freight" },
                  { label: "CM1", key: "cm1", bold: true },
                  { label: "CM1 Margin", key: "cm1_margin", pct: true },
                  { label: "Advertising", key: "advertising" },
                  { label: "Commission", key: "marketplace_commission" },
                  { label: "CM2", key: "cm2", bold: true, highlight: true },
                  { label: "CM2 Margin", key: "cm2_margin", pct: true, color: "text-emerald-400" },
                ].map((row) => (
                  <tr key={row.key} className={`border-b border-slate-800/20 ${row.highlight ? "bg-slate-800/20" : ""}`}>
                    <td className={`py-1.5 px-3 text-xs ${row.bold ? "font-semibold text-slate-200" : "text-slate-400"} ${row.color || ""}`}>{row.label}</td>
                    {periods.map((p) => {
                      const v = ch[row.key]?.[p];
                      return (
                        <td key={p} className={`py-1.5 px-3 text-right font-mono text-[11px] ${row.bold ? "font-semibold text-slate-200" : row.pct ? (row.color || "text-blue-400") : "text-slate-400"}`}>
                          {row.pct ? fPct(v) : v ? fM(v) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
